import React from 'react'
import { cn } from '@/utils/cn'

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'brand'

interface BadgeProps {
  children: React.ReactNode
  variant?: BadgeVariant
  size?: 'sm' | 'md'
  dot?: boolean
  className?: string
}

const variantClasses: Record<BadgeVariant, string> = {
  default:
    'bg-[var(--glass-medium)] border-[var(--border-subtle)] text-[var(--text-secondary)]',
  brand:
    'bg-[rgba(var(--color-brand-rgb),0.14)] border-[rgba(var(--color-brand-rgb),0.18)] text-[var(--color-brand)]',
  success:
    'bg-[rgba(16,185,129,0.12)] border-[rgba(16,185,129,0.18)] text-[var(--color-success)]',
  warning:
    'bg-[rgba(245,158,11,0.12)] border-[rgba(245,158,11,0.18)] text-[var(--color-warning)]',
  error:
    'bg-[rgba(239,68,68,0.12)] border-[rgba(239,68,68,0.18)] text-[var(--color-error)]',
  info: 'bg-[rgba(59,130,246,0.12)] border-[rgba(59,130,246,0.18)] text-[var(--color-info)]',
}

const dotColors: Record<BadgeVariant, string> = {
  default: 'bg-[var(--text-muted)]',
  brand: 'bg-[var(--color-brand)]',
  success: 'bg-[var(--color-success)]',
  warning: 'bg-[var(--color-warning)]',
  error: 'bg-[var(--color-error)]',
  info: 'bg-[var(--color-info)]',
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[14px] border font-semibold tracking-[0.01em]',
        size === 'sm' ? 'min-h-[22px] px-2.5 text-[10px]' : 'min-h-[28px] px-3 text-[11px]',
        variantClasses[variant],
        className,
      )}
    >
      {dot && (
        <span
          className={cn('w-1.5 h-1.5 rounded-full shrink-0', dotColors[variant])}
        />
      )}
      {children}
    </span>
  )
}
